import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { billingApi, instancesApi, type CartCheckoutItem, type ProductSKU } from '../api/client'
import { useAuthStore } from '../store/authStore'
import { useCartStore } from '../store/cartStore'
import { LEGAL_PATHS, type LegalLang } from './legalShared'
import { track } from '../lib/tracker'
import StorefrontNav from '../components/StorefrontNav'
import StorefrontFooter from '../components/StorefrontFooter'
import PaymentLogos from '../components/PaymentLogos'
import { CycleToggle } from './ProductsPage'

// Version stamped onto the order when the buyer ticks the agreement box. Bump
// it whenever the distance-sales text changes materially, so a dispute can be
// answered with the exact wording that was accepted.
const TERMS_VERSION = '2026-08-13'

// The cart is priced and charged in TRY: that is the currency the payment
// provider settles in, and showing anything else at the moment of payment
// would misstate what is being charged.
function formatTRY(minor: number): string {
  return `${(minor / 100).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}₺`
}

export default function CartPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const lang: LegalLang = i18n.language?.startsWith('tr') ? 'tr' : 'en'
  const paths = LEGAL_PATHS[lang]

  const lines = useCartStore((s) => s.lines)
  const cycle = useCartStore((s) => s.cycle)
  const setCycle = useCartStore((s) => s.setCycle)
  const setQuantity = useCartStore((s) => s.setQuantity)
  const setInstance = useCartStore((s) => s.setInstance)
  const remove = useCartStore((s) => s.remove)

  const [termsAccepted, setTermsAccepted] = useState(false)
  const [error, setError] = useState('')

  const { data: catalog } = useQuery({ queryKey: ['catalog'], queryFn: billingApi.getCatalog })
  // Only needed to resolve the mail add-on's target instance. Gated on auth:
  // the cart is a public page, and firing an authenticated request for an
  // anonymous visitor would trip the 401 refresh/redirect path.
  const needsInstances = lines.some((l) => l.sku === 'mail')
  const { data: instances } = useQuery({
    queryKey: ['instances'],
    queryFn: instancesApi.list,
    enabled: isAuthenticated && needsInstances,
  })

  const priced = lines.map((line) => {
    const product = catalog?.products.find((p) => p.sku === line.sku)
    const unitMinor = product
      ? Math.round((cycle === 'yearly' ? product.yearly_try : product.monthly_try) * 100)
      : 0
    return { ...line, product, unitMinor, lineMinor: unitMinor * line.quantity }
  })

  const totalMinor = priced.reduce((n, l) => n + l.lineMinor, 0)
  const vatRate = catalog?.products[0]?.vat_rate ?? 20
  // Prices are VAT-inclusive, so the VAT share is extracted from the total
  // rather than added on top.
  const vatMinor = Math.round(totalMinor - totalMinor / (1 + vatRate / 100))
  const netMinor = totalMinor - vatMinor

  const missingInstance = priced.some((l) => l.product?.needs_instance && !l.instanceId)

  const checkout = useMutation({
    mutationFn: () => {
      const items: CartCheckoutItem[] = priced.map((l) => ({
        sku: l.sku as ProductSKU,
        quantity: l.quantity,
        instance_id: l.instanceId,
      }))
      return billingApi.cartCheckout(items, cycle, TERMS_VERSION)
    },
    onSuccess: (data) => {
      window.location.href = data.payment_page_url
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        t('cart.checkoutError')
      setError(msg)
    },
  })

  const handleCheckout = () => {
    setError('')
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    track('checkout_started')
    checkout.mutate()
  }

  return (
    <div className="relative min-h-screen bg-[#fbfbfa] flex flex-col">
      <StorefrontNav />

      <div className="relative flex-1 max-w-5xl w-full mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-8">{t('cart.title')}</h1>

        {lines.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
            <p className="text-gray-500 mb-5">{t('cart.empty')}</p>
            <Link
              to="/urunler"
              className="inline-block px-6 py-3 rounded-xl text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
            >
              {t('cart.browseProducts')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start">
            {/* Lines */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{t('cart.cycleLabel')}</span>
                <CycleToggle cycle={cycle} onChange={setCycle} />
              </div>

              {priced.map((line) => (
                <div key={line.sku} className="bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h2 className="font-semibold text-gray-900">{t(`products.${line.sku}.name`)}</h2>
                      <p className="text-sm text-gray-500 mt-0.5">{t(`products.${line.sku}.tagline`)}</p>
                      <p className="text-sm text-gray-400 mt-2">
                        {formatTRY(line.unitMinor)} /{' '}
                        {cycle === 'yearly' ? t('pricing.yr') : t('pricing.mo')}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{formatTRY(line.lineMinor)}</div>
                      <button
                        onClick={() => remove(line.sku)}
                        className="mt-2 text-xs text-red-600 hover:text-red-700"
                      >
                        {t('cart.remove')}
                      </button>
                    </div>
                  </div>

                  {(line.product?.max_quantity ?? 1) > 1 && (
                    <div className="mt-4 flex items-center gap-2">
                      <span className="text-sm text-gray-600 mr-2">{t('products.quantity')}</span>
                      <button
                        onClick={() => setQuantity(line.sku, line.quantity - 1, line.product?.max_quantity)}
                        className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-100 font-bold"
                        aria-label={t('products.decrease')}
                      >
                        -
                      </button>
                      <span className="w-8 text-center font-semibold text-gray-900">{line.quantity}</span>
                      <button
                        onClick={() => setQuantity(line.sku, line.quantity + 1, line.product?.max_quantity)}
                        className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-100 font-bold"
                        aria-label={t('products.increase')}
                      >
                        +
                      </button>
                    </div>
                  )}

                  {line.product?.needs_instance && (
                    <div className="mt-4">
                      <label className="block text-sm text-gray-600 mb-1.5">{t('cart.pickInstance')}</label>
                      {!isAuthenticated ? (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          {t('cart.loginToPickInstance')}
                        </p>
                      ) : (instances?.length ?? 0) === 0 ? (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          {t('cart.noInstanceForMail')}
                        </p>
                      ) : (
                        <select
                          value={line.instanceId ?? ''}
                          onChange={(e) => setInstance(line.sku, e.target.value)}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 bg-white"
                        >
                          <option value="">{t('cart.selectPlaceholder')}</option>
                          {instances?.map((inst) => (
                            <option key={inst.id} value={inst.id}>
                              {inst.name} ({inst.subdomain})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <Link to="/urunler" className="inline-block text-sm text-brand-600 hover:text-brand-700">
                ← {t('cart.continueShopping')}
              </Link>
            </div>

            {/* Summary */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 lg:sticky lg:top-24">
              <h2 className="font-semibold text-gray-900 mb-4">{t('cart.summary')}</h2>

              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">{t('cart.subtotal')}</dt>
                  <dd className="text-gray-800">{formatTRY(netMinor)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">{t('cart.vat', { rate: vatRate })}</dt>
                  <dd className="text-gray-800">{formatTRY(vatMinor)}</dd>
                </div>
                <div className="flex justify-between pt-3 mt-3 border-t border-gray-100">
                  <dt className="font-semibold text-gray-900">{t('cart.total')}</dt>
                  <dd className="font-bold text-gray-900 text-lg">{formatTRY(totalMinor)}</dd>
                </div>
              </dl>

              <p className="mt-3 text-xs text-gray-400">
                {cycle === 'yearly' ? t('cart.renewsYearly') : t('cart.renewsMonthly')}
              </p>
              <p className="mt-1 text-xs text-gray-400">{t('cart.chargedInTRY')}</p>

              <label className="mt-5 flex items-start gap-2.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span>
                  <Link to={paths.distance} target="_blank" className="text-brand-600 hover:underline">
                    {t('footer.distanceSales')}
                  </Link>
                  {t('cart.termsAnd')}
                  <Link to={paths.delivery} target="_blank" className="text-brand-600 hover:underline">
                    {t('footer.deliveryReturn')}
                  </Link>
                  {t('cart.termsAccept')}
                </span>
              </label>

              {error && (
                <p className="mt-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              {missingInstance && (
                <p className="mt-4 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2">
                  {t('cart.pickInstanceWarning')}
                </p>
              )}

              <button
                onClick={handleCheckout}
                disabled={!termsAccepted || missingInstance || checkout.isPending || totalMinor <= 0}
                className="mt-4 w-full py-3 rounded-xl text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkout.isPending ? t('cart.redirecting') : t('cart.checkout')}
              </button>

              <div className="mt-5 pt-5 border-t border-gray-100">
                <PaymentLogos withCaption />
              </div>
            </div>
          </div>
        )}
      </div>

      <StorefrontFooter />
    </div>
  )
}
